// Vendors
import React, { useState } from 'react';
import { Tooltip, Typography } from '@mui/material';

// Components
import Pagination from '@/components/atoms/Pagination/Pagination';
import ReusablePopup from '@/components/organism/PopUp/ReusablePopup/ReusablePopup';
import { POPUP_TYPES } from '@/utils/constants';
import Loader from '@/components/atoms/Loader/Loader';

// Assets
import infoIcon from '@/assets/icons/infoIcon.svg';

interface policyBillingData {
  title: string;
  subtitle: string;
  headers: string[];
  rows: string[][];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: policyBillingData | null;
  loading: boolean;
}

const PolicyBillingPopup: React.FC<Props> = ({ isOpen, onClose, data, loading }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 7;

  const totalItems = data ? data.rows.length : 0;
  const currentRows = data?.rows.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  const invoiceDueIndex =
    data?.headers.findIndex((h) => h.toLowerCase() === 'invoice due date') ?? -1;
  const effectiveDateIndex =
    data?.headers.findIndex((h) => h.toLowerCase().includes('policy effective')) ?? -1;
  const expirationDateIndex =
    data?.headers.findIndex((h) => h.toLowerCase().includes('policy expiration')) ?? -1;
  const originalInvoiceAmountIndex =
    data?.headers.findIndex((h) => h.toLowerCase() === 'original invoice amount') ?? -1;
  const originalInsuranceIndex =
    data?.headers.findIndex((h) => h.toLowerCase() === 'original ins. premium') ?? -1;
  const formatInvoiceDueDate = (value: string): string => {
    if (/^\d{8}$/.test(value)) {
      const year = value.substring(0, 4);
      const month = value.substring(4, 6);
      const day = value.substring(6, 8);
      return `${month}/${day}/${year}`;
    }
    return value;
  };
  const formatDateYear = (DateStr: string) => {
    const date = new Date(DateStr);
    return (
      `${(date.getMonth() + 1).toString().padStart(2, '0')}/` +
      `${date.getDate().toString().padStart(2, '0')}/` +
      date.getFullYear().toString()
    );
  };

  const formatAmount = (value: string): string => {
    if (!value) return '';
   
    const clean = value.replace(/,/g, '').trim();
    const digitsOnly = clean.replace(/[^\d]/g, '');
   
    if (digitsOnly.length < 3) {
      const padded = digitsOnly.padStart(3, '0');
      const integerPart = padded.slice(0, -2);
      const decimalPart = padded.slice(-2);
      const number = parseFloat(`${integerPart}.${decimalPart}`);
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
   
    const integerPart = digitsOnly.slice(0, -2);
    const decimalPart = digitsOnly.slice(-2);
   
    const number = parseFloat(`${integerPart}.${decimalPart}`);
    if (isNaN(number)) return value;
   
    return number.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  return (
    <ReusablePopup
      isOpen={isOpen}
      onClose={onClose}
      typeClassName="contract-list-popup policy-tree"
      header={data?.title}
      type={POPUP_TYPES.POLICY_BILLING_DETAILS}
      isPaginationShown={totalItems > rowsPerPage}
    >
      <Typography className="sub-title">
        The amounts shown do not reflect any policy re-rating adjustments.
      </Typography>
      {loading ? (
        <div className="popup-loader">
          <Loader fullScreen={false} />
        </div>
      ) : (
        <>
          <table className="popup-table">
            <thead>
              <tr>
                {data?.headers.map((h, i) => (
                  <th key={i}>
                    {h}
                    {h === 'Original Invoice Amount' && (
                      <Tooltip
                        placement="top-start"
                        arrow
                        title="Insurance Premium + Billing fee + Interest if applicable"
                        classes={{
                          tooltip: 'custom_tooltip_wrap',
                          arrow: 'custom_tooltip_arrow',
                        }}
                      >
                        <img src={infoIcon} alt="info-icon" className="info-icon" />
                      </Tooltip>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows?.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, cellIdx) => {
                    let formattedCell = cell;

                    if (cellIdx === invoiceDueIndex) {
                      formattedCell = formatInvoiceDueDate(cell);
                    } else if (cellIdx === effectiveDateIndex || cellIdx === expirationDateIndex) {
                      formattedCell = formatDateYear(cell);
                    }
                    if (cellIdx === originalInvoiceAmountIndex) {
                      formattedCell = cell;
                    }
                    if (cellIdx === originalInsuranceIndex) {
                      formattedCell = formatAmount(cell);
                    }

                    return <td key={cellIdx}>{formattedCell}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {totalItems > rowsPerPage && (
            <div className="pagination_wrap">
              <div />
              <Pagination
                totalItems={totalItems}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </ReusablePopup>
  );
};

export default PolicyBillingPopup;
